import { NextPage } from "next";
import { INextPageProps } from "app";
import { useTranslations } from "next-intl";
import Seo from "domain/SeoAndAnalytics/Seo";
import { Category } from "domain/BlogAndNews/Category";
import { ContactCard } from "@carfax/web-ui/components/ContactCard";
import Breadcrumb from "components/Accessories/Breadcrumb";
import VinWidget from "components/VinSearch/VinWidget";
import { Breadcrumbs } from "utils/breadcrumbs";
import BlogContentApi from "@carfax/contentful/blog-content-api";
import { loadServerTranslations } from "../../i18n/messages";
import { languageConfig } from "i18n/language-config";
import { Config } from "utils/config/config";
import { Language } from "i18n/country";
import { Container, Variant } from "@carfax/web-ui/elements/Container";
import { useRoutes } from "domain/Routing/hooks/useRoutes";
import { frontendUrlBuilder } from "../../utils/routing/frontend-url-builder";
import { Icons } from "@carfax/web-ui/elements/Icon";
import { linkUtil } from "utils/i18n/link-util";
import { BlogPost, type NewsPostsResult } from "contentful/blog-content-api";
import { styles } from "../../styles/pageCommonStyles.style";
import { useHrefLangAlternatesForRouteSlug } from "domain/SeoAndAnalytics/hrefLangAlternates";

interface IProps extends INextPageProps {
  blogPosts: NewsPostsResult;
  locale: string;
}

const PressOverview: NextPage<IProps> = ({ blogPosts, locale }) => {
  const tCommon = useTranslations("common");
  const tPress = useTranslations("page-press");
  const routes = useRoutes();
  const canonicalLink = frontendUrlBuilder.buildCanonicalUrl(linkUtil.i18nLink(routes.press));
  const homeUrl = frontendUrlBuilder.buildCanonicalUrl(linkUtil.i18nLink("/"));

  const pressItems =
    blogPosts?.entries.map((blogPost: BlogPost) => ({
      headline: blogPost.title,
      url: frontendUrlBuilder.buildCanonicalUrl(linkUtil.i18nLink(blogPost.linkUrl)),
    })) || [];

  const schema = {
    pressListGraph: {
      canonicalUrl: canonicalLink,
      pageTitle: tPress("seo.metaTitle"),
      metaDescription: tPress("seo.metaDescription"),
      locale: locale || "en-US",
      homeLabel: tCommon("breadcrumb.home"),
      homeCanonicalUrl: homeUrl,
      blogLabel: tCommon("breadcrumb.press"),
      pressItems,
    },
  };

  const hrefLangAlternates = useHrefLangAlternatesForRouteSlug("press");

  return (
    <>
      <Seo
        title={tPress("seo.metaTitle")}
        description={tPress("seo.metaDescription")}
        schema={schema}
        hrefLangAlternates={hrefLangAlternates}
      />
      <Container paddingTop={Variant.Top.Standard}>
        <div className={styles.breadcrumbWrapper()}>
          <Breadcrumb items={[Breadcrumbs.home(tCommon), Breadcrumbs.press(tCommon, routes)]} />
        </div>
        <Category
          categoryIconSrc={Icons.news}
          categoryIconAlt={"ic-news.svg"}
          categoryIconTitle={tPress("pressCategory.iconTitle")}
          categoryTitle={tPress("pressCategory.title")}
          categoryText={tPress("pressCategory.text")}
          categoryFeatured={{
            url: "/static/images/press/pr-press-page-banner.jpg",
            title: "page-press:pressCategory.title",
          }}
          blogPosts={blogPosts.entries}
        />
        <div className={styles.contactCardWrapper()}>
          <ContactCard
            title={tCommon("press.contactCard.title")}
            name={tCommon("press.contactCard.name")}
            jobTitle={tCommon("press.contactCard.jobTitle")}
            emailLink={tCommon("press.contactCard.emailLink")}
            featuredImage="/static/images/press/contact-card.jpg"
            followHeadline={tCommon("press.contactCard.followHeadline")}
            socials={{
              facebook: Config.CARFAX_FACEBOOK_URL,
              linkedin: Config.CARFAX_LINKED_IN_URL,
              twitter: Config.CARFAX_TWITTER_URL,
            }}
          />
        </div>
      </Container>
      <VinWidget />
    </>
  );
};

export const getServerSideProps = async ({ params }) => {
  const lang = params?.lang || Language.ENGLISH;
  const locale = languageConfig.mapLanguageToLocale(lang);

  const contentful = BlogContentApi.getInstance();
  const blogPosts = await contentful.getNewsPosts(locale);

  return {
    props: {
      locale,
      messages: await loadServerTranslations(locale, ["common", "page-press"]),
      blogPosts,
    },
  };
};

export default PressOverview;
